/**
 * Reading money out of a promotion's `rewardRules`, and the wagering target it
 * implies before a claim exists.
 *
 * Three copies of this arithmetic existed — `PromoCard`'s `toBig`/`multBy` and
 * `TermTooltip`'s `toBigOrNull`/inline target — plus two test files that
 * re-declared them locally rather than importing them, so neither copy was
 * actually policed and the two could drift apart on the same screen.
 */

/**
 * `rewardRules` is `t.Record(t.String(), t.Unknown())` on the wire — the server
 * validates nothing about its contents — so a bare `BigInt()` here would
 * white-screen the app exactly as WEB-24 did on the promo card.
 */
export function toBigOrNull(v: unknown): bigint | null {
  if (v == null || v === '') return null;
  if (typeof v === 'bigint') return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    // Exact for a plain integer; anything else goes through Number, which is
    // fine for a display headline and cannot throw.
    if (/^-?\d+$/.test(trimmed)) return BigInt(trimmed);
    const n = Number(trimmed);
    return Number.isFinite(n) ? BigInt(Math.round(n)) : null;
  }
  if (typeof v === 'number') return Number.isFinite(v) ? BigInt(Math.round(v)) : null;
  return null;
}

/**
 * `bonusMinor × multiplier`, in minor units.
 *
 * `turnover_multiplier` is `numeric(6,2)`, so rounding it to a whole number
 * disclosed a 1.5x promo to the player as 2x — a 33% overstatement of what they
 * must wager before the bonus unlocks (WEB-26). Two decimals, and rounded UP,
 * because that is what `apps/promo/src/repo/turnover.ts` enforces: truncating
 * here stated a target up to one minor unit BELOW the one the server applies,
 * and for a requirement it is understating that surprises the player.
 */
export function wageringTargetMinor(bonusMinor: bigint, multiplier: number): bigint {
  if (bonusMinor <= 0n) return 0n;
  const hundredths = BigInt(Math.round(multiplier * 100));
  if (hundredths <= 0n) return 0n;
  return (bonusMinor * hundredths + 99n) / 100n;
}

/**
 * The rates and cap a rebate promotion is actually priced with.
 *
 * Mirror of `parseRateConfig` in `apps/promo/src/repo/rebate-calc.ts`, legacy
 * `pct` / `cap` fallbacks included. Rates are integer basis points (50 =
 * 0.50%); the cap is minor units, and 0 means no cap. A card that reads these
 * any other way discloses a rate the engine will not pay.
 */
export interface RebateRateConfig {
  defaultBips: number;
  gameTypeBips: Record<string, number>;
  /**
   * Rates keyed by provider id, which `computeTurnoverRebate` reads BEFORE both
   * of the above — so an entry here is what a card's rate lines are measured
   * against, not an extra on top of them.
   */
  providerBips: Record<string, number>;
  capMinor: bigint;
}

function intOf(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
    return Math.trunc(Number(v));
  }
  return fallback;
}

function intMap(v: unknown): Record<string, number> {
  if (!v || typeof v !== 'object') return {};
  const out: Record<string, number> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k] = intOf(val, 0);
  return out;
}

/**
 * Stricter than `toBigOrNull` on purpose: it is the server's `bigOf`, which
 * refuses a fractional string outright instead of rounding it. Whether the cap
 * parses is whether a cap EXISTS, so rounding one the engine reads as absent
 * would put a ceiling on the card that no payout applies.
 */
function capOf(v: unknown): bigint {
  if (typeof v === 'bigint') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return BigInt(Math.trunc(v));
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return BigInt(v.trim());
  return 0n;
}

export function rebateRateConfig(
  rewardRules: Record<string, unknown> | null | undefined,
): RebateRateConfig {
  const rr = rewardRules ?? {};
  return {
    defaultBips:
      rr['defaultBips'] != null
        ? intOf(rr['defaultBips'])
        : rr['pct'] != null
          ? intOf(Number(rr['pct']) * 100)
          : 0,
    gameTypeBips: intMap(rr['gameTypeBips']),
    providerBips: intMap(rr['providerBips']),
    capMinor: capOf(rr['capMinor'] ?? rr['cap']),
  };
}
